<template>
  <div v-if="composition" class="publishing-products">
    <div class="publishing-image-column">
      <div :key="updateTimestamp" class="publishing-image-wrapper">
        <PublishingPreviewImage
          :composition="composition"
          :design-image-only="
            !composition.sellables?.length || mode === CompositionMode.ADVANCED
          "
        />
      </div>
      <!-- Here rather than in the mode-specific properties cards: outside the mode branch it serves
           all three, UNSUPPORTED included, from one placement. -->
      <SampleOrderButton
        class="sample-order-btn"
        :source="SAMPLE_ORDER_SOURCE.COMPOSITION_PAGE"
      />
    </div>

    <div class="publishing-properties">
      <PublishingPropertiesSmart
        v-if="mode === CompositionMode.SMART"
        @open-product-settings="openProductSettingsDialog"
      />
      <PublishingPropertiesAdvanced
        v-else-if="mode === CompositionMode.ADVANCED"
        @open-product-settings="openProductSettingsDialog"
      />
      <span v-else-if="mode === CompositionMode.UNSUPPORTED">
        <ConfigList>
          <ul>
            <li>
              <div>
                <p>
                  {{
                    $t('PUBLISHING.DETAIL.PRODUCTS.PRODUCTS_CREATED.HEADING')
                  }}
                </p>
                <p class="text-sm text-grey">
                  {{
                    $t('PUBLISHING.DETAIL.PRODUCTS.PRODUCTS_CREATED.SUBHEADING')
                  }}
                </p>
              </div>
              <div>
                {{ composition.sellables.length }}
              </div>
            </li>
          </ul>
        </ConfigList>
        <Warning
          class="unsupported-hint"
          heading="PUBLISHING.DETAIL.PRODUCTS.UNSUPPORTED.BANNER.HEADING"
          subheading="PUBLISHING.DETAIL.PRODUCTS.UNSUPPORTED.BANNER.SUBHEADING"
          primary-action-text="PUBLISHING.DETAIL.PRODUCTS.UNSUPPORTED.BANNER.CTA"
          :primary-action-disabled-text="
            dirty
              ? 'PUBLISHING.DETAIL.PRODUCTS.UNSUPPORTED.BANNER.CTA_DISABLED_HINT'
              : undefined
          "
          @on-primary-action="resetProductSettings"
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCompositionStore } from 'commons/stores/composition';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import PublishingProductSettingsDialog from './productSettings/PublishingProductSettingsDialog.vue';
import Warning from 'src/app/components/warnings/Warning.vue';
import ProductSettingsResetDialog from './productSettings/ProductSettingsResetDialog.vue';
import { CompositionMode } from '@spreadgroup/common-partner-lib/composition';
import PublishingPreviewImage from 'src/app/partnerarea/publishing/detail/PublishingPreviewImage.vue';
import PublishingPropertiesSmart from 'src/app/partnerarea/publishing/detail/PublishingPropertiesSmart.vue';
import PublishingPropertiesAdvanced from './PublishingPropertiesAdvanced.vue';
import ConfigList from 'src/app/components/publishing/ConfigList.vue';
import SampleOrderButton from './SampleOrderButton.vue';
import { SAMPLE_ORDER_SOURCE } from 'commons/sampleOrder/sampleOrderTracking';

const compositionStore = useCompositionStore();

const updateTimestamp = ref(Date.now());
const showProgressBar = ref(true);

const composition = computed(() => compositionStore.current);
const mode = computed(() => compositionStore.mode);

const dirty = computed(() => compositionStore.dirty);

const resetProductSettings = async () => {
  await dialogService.openDialog(ProductSettingsResetDialog);
  showProgressBar.value = true;
  updateTimestamp.value = Date.now();
};

const openProductSettingsDialog = () => {
  dialogService
    .openDialog(PublishingProductSettingsDialog, null, {
      fullSize: true,
    })
    .then(() => {
      showProgressBar.value = true;
      // Rerender sellable images to update them immediately
      updateTimestamp.value = Date.now();
    });
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.publishing-products {
  display: flex;

  .publishing-image-column {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 35%;
    min-width: 200px;
    margin-right: 24px;
  }

  .sample-order-btn {
    margin-top: 16px;
  }
}

.publishing-properties {
  width: 100%;

  .edit-btn {
    margin: 16px 0 0 0;
  }
}

.sellables-link {
  font-weight: normal;
  color: $grey80;

  .icon {
    margin-left: 8px;
  }
}

.unsupported-hint {
  margin-top: 16px;
}

ul {
  padding-bottom: 8px;
  border-bottom: 1px solid $grey10;
}
</style>
